import type { ExamStatus } from '@dru/types';

/**
 * The words a worker reads, kept apart from the staff consoles' words.
 *
 * `EXAM_STATUS_LABEL` in @dru/types is written for HR and operations — it says
 * "Routed to partner" and "Awaiting physician", which are true and mean nothing
 * to somebody on a shop floor. The state machine keeps its names; this app
 * says what each one means for the person holding the phone.
 */
export const WORKER_STATUS_LABEL: Record<ExamStatus, string> = {
  DRAFT: 'Being arranged',
  REQUESTED: 'Being arranged',
  ROUTED: 'Being arranged',
  CONSENT_PENDING: 'Waiting for your consent',
  SCHEDULED: 'Appointment booked',
  NO_SHOW_OPEN: 'Appointment missed',
  NO_SHOW_CLOSED: 'Appointment missed',
  CHECKED_IN: 'Checked in',
  IN_PROGRESS: 'Tests under way',
  LAB_PENDING: 'Waiting for test results',
  REVIEW_PENDING: 'Doctor is checking your results',
  RETEST_REQUIRED: 'A test needs repeating',
  CERTIFIED: 'Completed',
  EXPIRED: 'Closed',
  CANCELLED: 'Cancelled',
};

export function workerStatus(status: string): string {
  return WORKER_STATUS_LABEL[status as ExamStatus] ?? 'In progress';
}

/** What kind of checkup, in words. Never the enum, even lower-cased. */
export const CHECKUP_TYPE_LABEL: Record<string, string> = {
  PRE_EMPLOYMENT: 'Pre-employment checkup',
  PERIODIC_OHC: 'Periodic health checkup',
  SPECIAL: 'Special examination',
  RETURN_TO_WORK: 'Return-to-work checkup',
  POST_INCIDENT: 'Checkup after an incident',
  EXIT: 'Exit checkup',
  RETEST: 'Repeat test',
  ANNUAL_HEALTH_CHECKUP: 'Annual health checkup',
};

export function checkupType(type: string | null | undefined): string {
  if (!type) return 'Health checkup';
  return CHECKUP_TYPE_LABEL[type] ?? 'Health checkup';
}

/**
 * Dates, one way everywhere: day first, month in words — "1 Oct 2026".
 *
 * Every call here used the browser's own locale (`undefined`), so a phone set
 * to US English — most Android handsets sold in India are — printed
 * "October 1, 2026" and "Thursday, October 1" while the certificate, the
 * reports and every staff screen said "1 Oct 2026". Fixed to en-IN, the same
 * as the consoles (apps/saas/src/lib/format.ts): the worker compares this
 * screen with the paper in their hand, and the two must read alike.
 *
 * A month name cannot be read the wrong way round, which is why none of these
 * ever prints a numeric month.
 */
const LOCALE = 'en-IN';

function parsed(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 1 Oct 2026 */
export function shortDate(value: string | null | undefined): string {
  return parsed(value)?.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' }) ?? '';
}

/** 1 October 2026 */
export function longDate(value: string | null | undefined): string {
  return parsed(value)?.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' }) ?? '';
}

/** 1 Oct 2026, 10:00 am */
export function dateTime(value: string): string {
  return parsed(value)?.toLocaleString(LOCALE, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }) ?? '';
}

/** Thursday, 1 October — and the year where the day is not this year's. */
export function weekdayDate(value: string | null | undefined, opts: { year?: boolean } = {}): string {
  return parsed(value)?.toLocaleDateString(LOCALE, {
    weekday: 'long', day: 'numeric', month: 'long', ...(opts.year ? { year: 'numeric' } : {}),
  }) ?? '';
}

/** Thu, 1 Oct, 10:00 am — an appointment still ahead, on one line. */
export function weekdayDateTime(value: string | null | undefined): string {
  return parsed(value)?.toLocaleString(LOCALE, {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  }) ?? '';
}

/** 10:00 am */
export function timeOfDay(value: string | null | undefined): string {
  return parsed(value)?.toLocaleTimeString(LOCALE, { hour: 'numeric', minute: '2-digit' }) ?? '';
}

/** Statuses that mean a checkup is over, one way or another. */
export const CLOSED_STATUSES: readonly string[] = ['CERTIFIED', 'CANCELLED', 'EXPIRED', 'NO_SHOW_CLOSED'];

/**
 * Whether an appointment is still ahead of the worker.
 *
 * Home and Checkup showed the first appointment on the order whatever its
 * date, so a worker whose tests were done on Tuesday was still being told on
 * Friday to "carry a photo ID" to a visit that had already happened. The
 * whole of today counts as ahead: a morning slot is still today's instruction
 * at noon.
 */
export function isUpcoming(scheduledFor: string): boolean {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return new Date(scheduledFor).getTime() >= start.getTime();
}
